import { type DocumentData, where } from "firebase/firestore";
import { runInAction } from "mobx";
import { firestoreApi } from "../../core/apis/firestoreApi";
import BaseApi from "../../core/BaseApi";
import { DbInfoType } from "../../dev/DevsStore";
import { log } from "../../ui/log";
import CalendarItem from "../Calendar/data/CalendarItemModel";
import CheckboxModel from "../CheckboxModule/data/CheckboxModel";
import ExpenseItemModule from "../ExpenseModule/data/ExpenseItemModel";
import SettlementItemModule from "../ExpenseModule/data/SettlementItemModule";
import RatingModel from "../elements/rating/RatingModel";
import LocationModel from "../Location/LocationModel";
import PollOptionModel from "../PollModule/data/PollOptionModel";
import PollVoteModel from "../PollModule/data/PollVoteModel";
import RsvpUserModel from "../RsvpModule/data/RsvpUserModel";
import TrackerUserData from "../TrackerModule/data/TrackerUserData";
import type BaseModuleDataModel from "./BaseModuleDataModel";
import {
  type BaseModuleModelData,
  ModuleDataType,
} from "./BaseModuleDataModel";

export default class ModulesDataApi extends BaseApi<
  // biome-ignore lint/suspicious/noExplicitAny: Because
  BaseModuleDataModel<any>,
  BaseModuleModelData
> {
  /* PRIVATE APIS */

  watchDataForModuleUid(moduleUid: string) {
    this.stores.devsStore.addDbStats(DbInfoType.WatchAdded, this.db, moduleUid);
    if (__DEV__) {
      log.network(
        `Started watchDataForModuleUid from db ${this.db} moduleUid ${moduleUid}`,
      );
    }

    const unsubscribe = firestoreApi.subscribeToQuery(
      this.db,
      [where("moduleUid", "==", moduleUid)],
      (querySnapshot) => {
        runInAction(() => {
          const addData: BaseModuleDataModel<BaseModuleModelData>[] = [];
          const deleteData: string[] = [];

          querySnapshot
            .docChanges()
            .forEach((change: { doc: DocumentData; type: string }) => {
              if (!change.doc.metadata.hasPendingWrites) {
                this.stores.devsStore.addDbStats(
                  DbInfoType.WatchItem,
                  this.db,
                  change.doc.id,
                );
              }

              if (change.type === "added" || change.type === "modified") {
                try {
                  addData.push(this.fromFirestore(change.doc));
                } catch (e) {
                  console.error(e);
                }
              }

              if (change.type === "removed") {
                deleteData.push(change.doc.id);
              }
            });

          if (addData.length) {
            /*if (__DEV__ && ) {
          log.network("Got watchDataForModuleUid updated data", addData);
        }*/
            this.store.load(addData);
          }

          if (deleteData.length) {
            /*if (__DEV__) {
          log.network("Got watchDataForModuleUid delete data", deleteData);
        }*/

            deleteData.forEach((d) => {
              this.store.deleteItem(d);
            });
          }
        });
      },
    );

    return () => {
      this.stores.devsStore.addDbStats(
        DbInfoType.WatchRemoved,
        this.db,
        moduleUid,
      );
      unsubscribe();
    };
  }

  /* NEEDED BASE API OVERRIDES */

  get db() {
    return `${this.dbPrefix}modules_data`;
  }

  get store() {
    return this.stores.modulesDataStore;
  }

  // biome-ignore lint/suspicious/noExplicitAny: Because
  fromFirestore(res: DocumentData): BaseModuleDataModel<any> {
    const data = res.data();

    if (data.type === ModuleDataType.Checkbox) {
      return new CheckboxModel(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.CalendarItem) {
      return new CalendarItem(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.PollOption) {
      return new PollOptionModel(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.PollVote) {
      return new PollVoteModel(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.Location) {
      return new LocationModel(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.Rating) {
      return new RatingModel(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.Expense) {
      return new ExpenseItemModule(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.Settlement) {
      return new SettlementItemModule(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.TrackerItem) {
      return new TrackerUserData(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    if (data.type === ModuleDataType.RsvpUser) {
      return new RsvpUserModel(this.stores, this.api, {
        uid: res.id,
        ...data,
      });
    }

    throw Error(`Tried to create invalid module data with type ${data.type}`);
  }
}
